import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Divider,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// import AbhaCreateModal from "../AbhaCreateModal/AbhaCreateModal";
import AbhaCreateModal from "../AbhaCreateModal/AbhaCreateModal";
import AbhaProfileModal from "../AbhaProfileModal/AbhaProfileModal";
import { CommonContext } from "../../context/CommonContext";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { encryptTextApi, fetchAbdmAccessToken, formatAbhaInput, useResendOTP } from "../../helpers/commonHelper";
import { apisPath, paths } from "../../utils/path";
import { authHttp } from "../../services/commonHttpService";
import Step4AbhaRegistered from "../AbhaCreateModal/Step4AbhaRegistered/Step4AbhaRegistered";
import DemographicAuth from "./DemographicAuth";


const Abhasearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { isLoading, setIsLoading, setAlertState, setSnackState } = useContext(CommonContext);
  const { setAbhaProfile } = useContext(AuthContext);

  // State management
  const [searchBy, setSearchBy] = useState("abha");
  const [abhaId, setAbhaId] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);

  const [openAbha, setOpenAbha] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);

  // API related states
  const [abdmAccessToken, setAbdmAccessToken] = useState(null);

  // SEPARATE txnIds for clarity
  const [searchTxnId, setSearchTxnId] = useState(null); // From searchByMobile ONLY
  const [otpTxnId, setOtpTxnId] = useState(null);       // From OTP APIs (used for verification)
  const [failedTxnId, setFailedTxnId] = useState(null); // From failed verification (used for resend after failure)

  const [token, setToken] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [abhaData, setAbhaData] = useState(null);

  // New states for mobile flow with profile selection
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedAuthType, setSelectedAuthType] = useState("MOBILE_OTP");

  // State for ABHA authentication type
  const [abhaAuthType, setAbhaAuthType] = useState("aadhaar-verify");

  // Store encrypted payloads for resend (to avoid re-encrypting)
  const [encryptedAbhaId, setEncryptedAbhaId] = useState(null);
  const [encryptedMobileIndex, setEncryptedMobileIndex] = useState(null);

  // Resend OTP hook - Initialize with autoStart: false
  const {
    countdown,
    resendCount,
    canResend,
    formattedTime,
    handleResend,
    resetTimer,
    startTimer,
    maxResendCount
  } = useResendOTP({
    maxResendCount: 3,
    countdownSeconds: 60,
    autoStart: false,
    onResend: async () => {
      try {
        setIsLoading(true);

        if (searchBy === "mobile") {
          await resendMobileOtp();
        } else {
          await resendAbhaOtp();
        }

        setSnackState({
          open: true,
          message: "OTP resent successfully",
          severity: "success",
        });
      } catch (error) {
        throw error; // Let hook handle the error
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Fetch ABDM Access Token when modal opens
  useEffect(() => {
    if (open) {
      loadAbdmToken();
    }

    return () => {
      if (!open) {
        resetState();
      }
    };
  }, [open]);

  const loadAbdmToken = async () => {
    const token = await fetchAbdmAccessToken(setIsLoading, setSnackState);
    if (token) {
      setAbdmAccessToken(token);
    }
  };

  const resetState = () => {
    setSearchBy("abha");
    setAbhaId("");
    setMobileNumber("");
    setOtp("");
    setStep(1);

    setSearchTxnId(null);
    setOtpTxnId(null);
    setFailedTxnId(null);
    setToken(null);

    setAccounts([]);
    setAbhaData(null);
    setSelectedProfile(null);
    setSelectedAuthType("MOBILE_OTP");
    setAbhaAuthType("aadhaar-verify");

    setEncryptedAbhaId(null);
    setEncryptedMobileIndex(null);

    resetTimer();
  };

  /* ======================================================
   MOBILE FLOW
 ====================================================== */

  // 1️⃣ SEARCH BY MOBILE
  const handleSearchByMobile = async () => {
    if (!mobileNumber.trim()) {
      setSnackState({
        open: true,
        message: "Please enter mobile number",
        severity: "info",
      });
      return;
    }

    try {
      setIsLoading(true);

      const encryptedMobile = await encryptTextApi(mobileNumber.trim());

      const response = await authHttp.post(
        apisPath?.searchByMobile,
        { mobile: encryptedMobile },
        { headers: { accessToken: abdmAccessToken } }
      );

      if (response?.data?.success) {
        const data = response?.data?.data?.[0];

        if (!data?.txnId) {
          throw new Error("Search txnId missing from response");
        }

        // Store search txnId (used ONCE to call sendMobileOtp)
        setSearchTxnId(data?.txnId);

        // Check if ABHA profiles found
        if (data?.ABHA && Array.isArray(data?.ABHA) && data?.ABHA.length > 0) {
          setAccounts(data?.ABHA);
          setTimeout(() => setOpenProfile(true), 300);
        } else {
          setSnackState({
            open: true,
            message: "No ABHA profiles found for this mobile number",
            severity: "info",
          });
        }
      } else {
        throw new Error(response?.data?.message || "Failed to search ABHA profiles");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.error
        ? error?.response?.data?.error?.error?.message
        : error?.response?.data?.message || error?.message;

      setSnackState({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 1️⃣.5️⃣ DEMOGRAPHIC AUTHENTICATION
  const handleDemographicAuth = async (payload) => {
    try {
      setIsLoading(true);
      // Encrypt Aadhaar number
      const encryptedAadhaar = await encryptTextApi(payload.aadhaarNumber);

      const requestPayload = {
        ...payload,
        aadhaarNumber: encryptedAadhaar
      };

      const response = await authHttp.post(
        apisPath?.userDemographicVerify,
        requestPayload,
        { headers: { accessToken: abdmAccessToken } }
      );

      if (response?.data?.success) {
        const data = response?.data?.data;
        // Demographic authentication returns the verified profile directly
        setAbhaData(data);
        setStep(4);
      } else {
        throw new Error(response?.data?.message || "Demographic authentication failed");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.error
        ? error?.response?.data?.error?.error?.message
        : error?.response?.data?.message || error?.message;

      setSnackState({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 2️⃣ SEND MOBILE OTP
  const sendMobileOtp = async (profile, authType) => {
    // Use stored encrypted value or encrypt for first time
    let encryptedIndex = encryptedMobileIndex;

    if (!encryptedIndex) {
      encryptedIndex = await encryptTextApi(profile.index.toString());
      setEncryptedMobileIndex(encryptedIndex);
    }

    const response = await authHttp.post(
      apisPath?.sendMobileOtp,
      {
        loginId: encryptedIndex,
        type: authType,
        txnId: searchTxnId, // Use searchTxnId from searchByMobile
      },
      { headers: { accessToken: abdmAccessToken } }
    );

    if (response?.data?.success) {
      const data = response?.data?.data;

      if (!data?.txnId) {
        throw new Error("OTP txnId missing from response");
      }

      // Store OTP session txnId (used for verification and resend)
      setOtpTxnId(data?.txnId);

      return data;
    } else {
      throw new Error(response?.data?.message || "Failed to send OTP");
    }
  };

  // 3️⃣ RESEND MOBILE OTP
  const resendMobileOtp = async () => {
    // CRITICAL: Uses otpTxnId which will be updated with failed txnId if verification fails
    // Do NOT use otpTxnId for resend!
    const txnIdToUse = failedTxnId || searchTxnId;

    const response = await authHttp.post(
      apisPath?.sendMobileOtp,
      {
        loginId: encryptedMobileIndex,
        type: selectedAuthType,
        txnId: txnIdToUse, // ✅ Use searchTxnId (or failedTxnId after failed verification)
      },
      { headers: { accessToken: abdmAccessToken } }
    );

    if (response?.data?.success) {
      const data = response?.data?.data;

      // Update otpTxnId with new txnId from resend response
      if (data?.txnId) {
        setOtpTxnId(data?.txnId);
      }

      return data;
    }

    throw new Error(response?.data?.message || "Failed to resend OTP");
  };

  // 4️⃣ VERIFY MOBILE OTP
  const verifyMobileOtp = async () => {
    const encryptedOtp = await encryptTextApi(otp);

    return authHttp.post(
      apisPath?.verifyMobileOtp,
      {
        type: selectedAuthType,
        txnId: otpTxnId, // Use otpTxnId for verification
        otpValue: encryptedOtp,
      },
      { headers: { accessToken: abdmAccessToken } }
    );
  };

  /* ======================================================
     ABHA FLOW
  ====================================================== */

  // SEND ABHA OTP
  const sendAbhaOtp = async () => {
    // Use stored encrypted value or encrypt for first time
    let encryptedValue = encryptedAbhaId;

    if (!encryptedValue) {
      encryptedValue = await encryptTextApi(abhaId.trim());
      setEncryptedAbhaId(encryptedValue);
    }

    const response = await authHttp.post(
      apisPath?.sendAbhaOtp,
      {
        loginId: encryptedValue,
        type: abhaAuthType
      },
      { headers: { accessToken: abdmAccessToken } }
    );

    if (response?.data?.success) {
      const data = response?.data?.data;

      if (!data?.txnId) {
        throw new Error("ABHA OTP txnId missing");
      }

      // Store OTP session txnId
      setOtpTxnId(data?.txnId);

      return data;
    } else {
      throw new Error(response?.data?.message || "Failed to send OTP");
    }
  };

  // RESEND ABHA OTP
  const resendAbhaOtp = async () => {
    const response = await authHttp.post(
      apisPath?.sendAbhaOtp,
      {
        loginId: encryptedAbhaId,
        type: abhaAuthType
      },
      { headers: { accessToken: abdmAccessToken } }
    );

    if (response?.data?.success) {
      const data = response?.data?.data;

      // CRITICAL: Update otpTxnId with new txnId from resend response
      if (data?.txnId) {
        setOtpTxnId(data?.txnId);
      }

      return data;
    }

    throw new Error(response?.data?.message || "Failed to resend OTP");
  };

  // VERIFY ABHA OTP
  const verifyAbhaOtp = async () => {
    const encryptedOtp = await encryptTextApi(otp);

    return authHttp.post(
      apisPath?.verifyAbhaOtp,
      {
        txnId: otpTxnId, // ✅ Use otpTxnId for verification
        otpValue: encryptedOtp,
        type: abhaAuthType
      },
      { headers: { accessToken: abdmAccessToken } }
    );
  };

  /* ======================================================
     🔶 COMMON HANDLERS
  ====================================================== */

  const handleSendOtp = async () => {
    try {
      setIsLoading(true);

      if (searchBy === "mobile") {
        // Mobile flow: just search, profile modal will handle OTP
        await handleSearchByMobile();
        return;
      }

      // ABHA flow: send OTP directly
      const res = await sendAbhaOtp();

      setSnackState({
        open: true,
        message: res?.message || "OTP sent successfully",
        severity: "success",
      });

      setStep(2);
      resetTimer();
      startTimer();
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to send OTP",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileNext = async (profile, authType) => {
    try {
      setIsLoading(true);
      setSelectedProfile(profile);
      setSelectedAuthType(authType);
      setOpenProfile(false);

      const res = await sendMobileOtp(profile, authType);

      setSnackState({
        open: true,
        message: res?.message || "OTP sent successfully",
        severity: "success",
      });

      setStep(2);
      resetTimer();
      startTimer();
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to send OTP",
        severity: "error",
      });

      // Reopen profile modal on error
      setTimeout(() => setOpenProfile(true), 300);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setSnackState({
        open: true,
        message: "Please enter 6-digit OTP",
        severity: "info",
      });
      return;
    }

    try {
      setIsLoading(true);

      const response =
        searchBy === "mobile" ? await verifyMobileOtp() : await verifyAbhaOtp();

      if (response?.data?.success) {
        const data = response?.data?.data;

        // CHECK authResult for BOTH mobile and ABHA flows
        if (data?.authResult === "failed") {
          if (data?.txnId) {
            // Update otpTxnId for next verification attempt (both flows)
            setOtpTxnId(data?.txnId);

            // For MOBILE: also store in failedTxnId for resend operations
            if (searchBy === "mobile") {
              setFailedTxnId(data?.txnId);
            }
          }

          // Clear OTP field for retry
          setOtp("");

          throw new Error(
            data?.message || "Invalid OTP. Please enter the correct OTP or request a new one."
          );
        }

        // SUCCESS - authResult === "success" for both flows
        if (data?.authResult === "success") {
          if (data?.token) setToken(data?.token);

          if (data?.accounts && Array.isArray(data?.accounts) && data?.accounts.length > 0) {
            await verifyAbhaUser(data?.accounts[0], data?.token);
          }

          // setAlertState({
          //   open: true,
          //   title: data?.message || "OTP verified successfully",
          //   status: "success",
          //   btnTxt: "OK",
          // });
        } else {
          throw new Error(response?.data?.message || "Failed to verify OTP");
        }
      } else {
        throw new Error(response?.data?.message || "Failed to verify OTP");
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to verify OTP",
        severity: "error",
      });

    } finally {
      setIsLoading(false);
    }
  };

  /* ======================================================
     VERIFY ABHA USER
  ====================================================== */
  const verifyAbhaUser = async (profile, authToken) => {
    try {
      setIsLoading(true);

      const response = await authHttp.post(
        apisPath?.verifyAbhaOtpUser,
        { token: authToken || token },
        { headers: { accessToken: abdmAccessToken } }
      );

      if (response?.data?.success) {
        const profileData = response?.data?.data;
        setAbhaData(profileData);
        setStep(4);
      } else {
        setAlertState({
          open: true,
          title: response?.data?.message,
          status: "success",
          btnTxt: "CLOSE",
          onClose: () => {
            handleClose();
          },
        });
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to verify ABHA user",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterBeneficiary = () => {
    if (abhaData) {
      setAbhaProfile(abhaData);
    }
    handleClose();
    navigate(`${paths?.user?.commonPoint}${paths?.user?.patientRegistration}`);
  };


  /* ======================================================
     HELPER FUNCTIONS
  ====================================================== */
  const handleOtpChange = (e, setFunction) => {
    let value = e.target.value.replace(/\D/g, ""); // Only digits
    if (value.length > 6) value = value.slice(0, 6);
    setFunction(value);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  /* ======================================================
     VALIDATIONS
  ====================================================== */
  const progressValue = step === 1 ? 33 : step === 2 ? 66 : 100;
  const isMobileValid = /^[6-9]\d{9}$/.test(mobileNumber);
  const isAbhaValid = abhaId.length === 17 && abhaId.replace(/\D/g, "").length === 14;
  const canProceedStep1 = searchBy === "mobile" ? isMobileValid : isAbhaValid;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "25px",
            padding: "10px 20px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.3rem" }}>
          {step === 4 ? "ABHA Registered" : "Search ABHA ID"}
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Description */}
          {step !== 4 && (
            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
              Search your ABHA using your Mobile Number or ABHA Number or Demographic Authentication.
            </p>
          )}

          {/* ==================== STEP 1: ENTER MOBILE/ABHA ==================== */}
          {step === 1 && (
            <>
              <RadioGroup
                row
                value={searchBy}
                onChange={(e) => {
                  setSearchBy(e.target.value);
                  setAbhaId("");
                  setMobileNumber("");
                }}
              >
                <FormControlLabel
                  value="mobile"
                  control={<Radio />}
                  label="Mobile Number"
                />
                <FormControlLabel value="abha" control={<Radio />} label="ABHA Number" />
                <FormControlLabel value="demographic" control={<Radio />} label="Demographic Authentication" />
              </RadioGroup>

              {searchBy === "abha" ? (
                <div className="mt-3">
                  <label className="fw-semibold">ABHA Number <span className="txtRed">*</span></label>
                  <div className="d-flex align-items-center gap-2 mt-1">
                    <TextField
                      variant="standard"
                      fullWidth
                      value={abhaId}
                      onChange={(e) => {
                        const formatted = formatAbhaInput(e.target.value);
                        setAbhaId(formatted);
                      }}
                      placeholder="XX-XXXX-XXXX-XXXX"
                      inputProps={{ maxLength: 17 }}
                      helperText={
                        abhaId.length > 0 && abhaId.length < 17
                          ? `Enter 14 digits valid ABHA Number`
                          : ""
                      }
                      error={abhaId.length > 0 && !isAbhaValid}
                    />
                    <Button
                      className="abhacreatebtn"
                      variant="outlined"
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "5px 15px",
                      }}
                      onClick={() => setOpenAbha(true)}
                    >
                      Create ABHA
                    </Button>
                  </div>

                  {/* Authentication Type Dropdown for ABHA */}
                  <FormControl fullWidth sx={{ mt: 3 }}>
                    <InputLabel id="abha-auth-type-label">Authentication type <span className="txtRed">*</span></InputLabel>
                    <Select
                      labelId="abha-auth-type-label"
                      value={abhaAuthType}
                      label={
                        <>
                          Authentication Type <span className="txtRed">*</span>
                        </>
                      }
                      onChange={(e) => setAbhaAuthType(e.target.value)}
                    >
                      <MenuItem value="aadhaar-verify">Aadhaar OTP</MenuItem>
                      <MenuItem value="mobile-verify">Mobile OTP</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      marginTop: 4,
                      background: "linear-gradient(90deg, #D063CA, #B056CC)",
                      textTransform: "none",
                      borderRadius: "25px",
                      padding: "10px 0",
                    }}
                    onClick={handleSendOtp}
                    disabled={!canProceedStep1}
                  >
                    Send OTP
                  </Button>
                </div>
              ) : searchBy === "mobile" ? (
                <div className="mt-3">
                  <label className="fw-semibold">Mobile Number <span className="txtRed">*</span></label>
                  <TextField
                    variant="standard"
                    fullWidth
                    value={mobileNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 10) {
                        setMobileNumber(value);
                      }
                    }}
                    placeholder="Enter your 10-digit mobile number"
                    inputProps={{ maxLength: 10 }}
                    error={mobileNumber.length > 0 && !isMobileValid}
                    helperText={
                      mobileNumber.length > 0 && mobileNumber.length < 10
                        ? "Mobile number must be 10 digits"
                        : mobileNumber.length === 10 && !isMobileValid
                          ? "Mobile number must start with 6, 7, 8, or 9"
                          : ""
                    }
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      marginTop: 4,
                      background: "linear-gradient(90deg, #D063CA, #B056CC)",
                      textTransform: "none",
                      borderRadius: "25px",
                      padding: "10px 0",
                    }}
                    onClick={handleSendOtp}
                    disabled={!canProceedStep1}
                  >
                    Send OTP
                  </Button>
                </div>
              ) : searchBy === "demographic" ? (
                <DemographicAuth
                  onSubmit={handleDemographicAuth}
                  isLoading={isLoading}
                />
              ) : null}
            </>
          )}

          {/* ==================== STEP 2: OTP VERIFICATION ==================== */}
          {step === 2 && (
            <>
              <div className="progress-header">
                <div className="progress-label">Progress</div>
                <div className="progress-step">{`Step ${step} of 3`}</div>
              </div>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                className="custom-progress"
              />

              <div className="step-content mt-4">
                <h6>OTP Verification</h6>
                <p className="small-text">
                  Enter the 6-digit OTP sent to your registered{" "}
                  {searchBy === "mobile" ? "mobile number" : "ABHA ID"}
                </p>
                <div className="otp-container">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => handleOtpChange(e, setOtp)}
                    maxLength={6}
                    className="otp-input"
                    placeholder="_ _ _  -   _ _ _"
                  />
                </div>
                <div className="d-flex gap-2 mt-3">
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      marginTop: 4,
                      background: "linear-gradient(90deg, #D063CA, #B056CC)",
                      textTransform: "none",
                      borderRadius: "25px",
                      padding: "10px 0",
                    }}
                    disabled={otp.length !== 6}
                    onClick={handleVerifyOtp}
                  >
                    Verify OTP
                  </Button>
                </div>
                {/* Resend OTP Section */}
                <div className="d-flex justify-content-between align-items-center mt-3" style={{ fontSize: '0.9rem' }}>
                  <span style={{ color: '#666' }}>Didn't receive OTP?</span>
                  <div className="d-flex align-items-center gap-3">
                    <button
                      onClick={async () => {
                        try {
                          await handleResend();
                        } catch (error) {
                          setSnackState({
                            open: true,
                            message: error?.message || 'Failed to resend OTP',
                            severity: 'error',
                          });
                        }
                      }}
                      disabled={!canResend}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: canResend ? '#B056CC' : '#999',
                        cursor: canResend ? 'pointer' : 'not-allowed',
                        textDecoration: canResend ? 'underline' : 'none',
                        fontWeight: 500,
                        padding: 0
                      }}
                    >
                      Resend OTP ({resendCount}/{maxResendCount})
                    </button>
                  </div>
                  <span style={{ color: '#666', fontSize: '0.85rem' }}>
                    {formattedTime} remaining
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ==================== STEP 4: ABHA PROFILE DETAILS ==================== */}
          {step === 4 && abhaData && (
            <Step4AbhaRegistered
              abhaData={abhaData}
              onRegisterBeneficiary={handleRegisterBeneficiary}
            />
          )}

          {/* OR Divider & QR Code Button */}
          {step !== 4 && (
            <>

              <Divider sx={{ my: 3 }}></Divider>
              {/* <Button
                variant="outlined"
                className="scan-abha-btn"
                fullWidth
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  padding: "10px 0",
                }}
              >
                <i className="bi bi-qr-code-scan me-2"></i> Scan ABHA QR code
              </Button> */}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== CHILD MODALS ==================== */}

      {/* ABHA Create Modal */}
      <AbhaCreateModal open={openAbha} onClose={() => setOpenAbha(false)} handleSearchModalClose={handleClose} />

      {/* ABHA Profile Selection Modal */}
      <AbhaProfileModal
        open={openProfile}
        onClose={() => setOpenProfile(false)}
        profiles={accounts}
        onNext={handleProfileNext}
      />
    </>
  );
};

export default Abhasearch;
